/**
 * Phase C 승인 클라이언트 (ADR 0028 · Issue #20)
 * --------------------------------------------------------------------------
 * 브라우저가 보내는 것은 **서버가 발급한 식별자**뿐입니다. 무엇을 실행할지는 서버가
 * 보관한 불변 스냅숏이 정하므로, 이 파일에는 대상·인자·patch를 만드는 코드가
 * 존재하지 않습니다.
 *
 * 네 가지가 이 모듈의 형태를 정합니다.
 *
 *   두 단계     DRY_RUN이 검토를 열고, CONFIRM이 **그 검토를 지목해서** 확정합니다.
 *   멱등 키     단계마다 하나이고, 재시도는 **같은 키**로 갑니다 — 다시 만들면
 *              재시도가 두 번째 실행이 됩니다.
 *   응답 재검증  서버가 이미 검증했지만 화면이 한 번 더 봅니다. 중간 프록시가
 *              바꿔치기한 프레임이 승인 상태 기계에 들어오면 안 됩니다.
 *   취소       요청은 AbortSignal을 따릅니다. 확정은 **제출 전까지만** 취소됩니다.
 *
 * 유일한 통로는 여전히 `@/api/client`입니다.
 */
import type { AIActionApprovalResponse, AIActionErrorCode } from "@k8s-dashboard/contracts";
import { AI_ACTION_ERROR_CODES, isSafeApprovalResponse } from "@k8s-dashboard/contracts";
import { HttpError, apiRequest } from "./client";

/** 승인 한 단계의 결과. 값이 없는 상태를 값과 뭉개지 않습니다. */
export type ApprovalOutcome =
  | { status: "ok"; response: AIActionApprovalResponse }
  | { status: "error"; code: AIActionErrorCode | "action_unavailable" };

const ERROR_CODES = new Set<string>(AI_ACTION_ERROR_CODES);

/**
 * 서버가 발급하지 않은 값으로 승인을 부르지 않습니다.
 * 계약과 같은 문법이며, 여기서 막으면 잘못된 경로가 만들어지지 않습니다.
 */
const SAFE_ID = /^[A-Za-z0-9._-]{1,128}$/;

/**
 * 멱등 키를 만듭니다.
 *
 * 단계와 대상마다 하나이고, **재시도는 같은 키를 씁니다.** 무작위 값을 매번 만들면
 * 서버는 그것을 새 요청으로 보고, 사용자가 한 번 누른 행동이 두 번 일어납니다.
 * 값은 브라우저가 만들지만 신원이 아니라 요청의 이름일 뿐이고, 실제 결합은 서버가
 * 소유자·제안·단계·본문으로 다시 만듭니다.
 */
export function idempotencyKey(recordId: string, stage: "DRY_RUN" | "CONFIRM", nonce: string): string {
  return `${stage === "DRY_RUN" ? "d" : "c"}-${recordId}-${nonce}`.slice(0, 128);
}

/** 암호학적 난수 nonce. 재시도 사이에 유지되어야 하므로 호출부가 보관합니다. */
export function approvalNonce(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function approve(
  recordId: string,
  body: Record<string, string>,
  key: string,
  signal?: AbortSignal,
): Promise<ApprovalOutcome> {
  if (!SAFE_ID.test(recordId) || !SAFE_ID.test(key)) return { status: "error", code: "action_invalid_request" };
  try {
    const response = await apiRequest<unknown>(`/api/ai/actions/${encodeURIComponent(recordId)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "content-type": "application/json", "Idempotency-Key": key },
      signal,
    });
    /* 서버가 이미 검증했더라도 화면이 다시 봅니다 — 서버 앞에 무엇이 있는지
       화면은 모릅니다. 계약 밖 응답은 값이 아니라 오류입니다. */
    if (!isSafeApprovalResponse(response)) return { status: "error", code: "action_unavailable" };
    return { status: "ok", response };
  } catch (error) {
    if (signal?.aborted) throw error;
    return { status: "error", code: errorCode(error) };
  }
}

/** 오류를 **계약의 닫힌 코드**로 좁힙니다. 모르는 값은 하나의 사유로 접습니다. */
function errorCode(error: unknown): AIActionErrorCode | "action_unavailable" {
  if (error instanceof HttpError) {
    const reason = error.body.reasonCode ?? error.body.code;
    if (reason && ERROR_CODES.has(reason)) return reason as AIActionErrorCode;
  }
  return "action_unavailable";
}

/** 검토를 엽니다. 실제 write는 일어나지 않습니다. */
export function startDryRun(recordId: string, key: string, signal?: AbortSignal): Promise<ApprovalOutcome> {
  return approve(recordId, { stage: "DRY_RUN" }, key, signal);
}

/**
 * 검토를 확정합니다. **서버가 발급한** 실행 id와 digest를 지목합니다 —
 * 사용자가 읽은 검토와 서버가 제출하는 것이 갈라질 수 없습니다.
 */
export function confirmApproval(
  recordId: string,
  executionId: string,
  dryRunDigest: string,
  key: string,
  signal?: AbortSignal,
): Promise<ApprovalOutcome> {
  return approve(recordId, { stage: "CONFIRM", executionId, dryRunDigest }, key, signal);
}
